import { useState, useEffect, useCallback } from "react"
import { useAuth } from "@/hooks/useAuth"
import {
  getUsageSummary,
  getUsageByUser,
  getUsageDaily,
  type UsageSummary,
  type UserUsage,
  type DailyUsage,
} from "@/services/usageService"

interface UseUsageStatsOptions {
  from: string
  to: string
  userId?: string
}

export function useUsageStats({ from, to, userId }: UseUsageStatsOptions) {
  const { token } = useAuth()
  const [summary, setSummary] = useState<UsageSummary | null>(null)
  const [userRanking, setUserRanking] = useState<UserUsage[]>([])
  const [dailyTrend, setDailyTrend] = useState<DailyUsage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!token) return
    setLoading(true)
    try {
      const [summaryData, byUserData, dailyData] = await Promise.all([
        getUsageSummary(token, from, to),
        getUsageByUser(token, from, to),
        getUsageDaily(token, from, to, userId),
      ])
      setSummary(summaryData)
      setUserRanking(byUserData.users || [])
      setDailyTrend(dailyData.daily || [])
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load usage stats")
    } finally {
      setLoading(false)
    }
  }, [token, from, to, userId])

  useEffect(() => {
    refresh()
  }, [refresh])

  return { summary, userRanking, dailyTrend, loading, error, refresh }
}
